import React, { useState, useMemo } from "react";
import "./DetailsTable.css";

export interface ColumnConfig<T> {
  header: string;
  accessor?: keyof T | string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
}

export interface DetailsTableProps<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  rowKey?: (item: T, index: number) => React.Key;
}

type SortDirection = "asc" | "desc";

const DetailsTable = <T,>({
  data,
  columns,
  rowKey = (_item, index) => index,
}: DetailsTableProps<T>) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (accessor?: keyof T | string, sortable?: boolean) => {
    if (!sortable || !accessor) return;

    if (sortColumn === accessor) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(accessor.toString());
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      const aVal = (a as any)[sortColumn];
      const bVal = (b as any)[sortColumn];

      if (aVal === undefined || bVal === undefined) return 0;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortDirection === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortColumn, sortDirection]);

  return (
    <div className="details-table-container">
      <table className="details-table">
        <thead>
          <tr>
            {columns.map((col, idx) => {
              const isSortable = col.sortable && col.accessor;
              const isSorted = sortColumn === col.accessor;
              return (
                <th
                  key={idx}
                  onClick={() => handleSort(col.accessor, col.sortable)}
                  className={isSortable ? "sortable" : ""}
                >
                  {col.header}
                  {isSorted ? (sortDirection === "asc" ? " ▲" : " ▼") : col.sortable ? " ⇅" : ""}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, rowIndex) => (
            <tr key={rowKey(item, rowIndex)}>
              {columns.map((col, colIndex) => (
                <td key={colIndex}>
                  {col.render
                    ? col.render(item)
                    : col.accessor
                    ? (item as any)[col.accessor]
                    : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DetailsTable;
